import {useMemo, useState} from "react";
import {SiteEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode"

export type UseSiteDataTableFilterProps = Omit<UseDataTableFilterProps<SiteEntity>, 'filterConfig'>

export function useSiteDataTableFilter(props: UseSiteDataTableFilterProps): UseDataTableFilterResult<SiteEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [routeQuery, setRouteQuery] = useState<string | undefined>(undefined);
  const [routeDebouncedQuery] = useDebouncedValue(routeQuery, 200);

  const [operatingAreaQuery, setOperatingAreaQuery] = useState<string | undefined>(undefined);
  const [operatingAreaDebouncedQuery] = useDebouncedValue(operatingAreaQuery, 200);

  const [typeQuery, setTypeQuery] = useState<string | undefined>(undefined);
  const [typeDebouncedQuery] = useDebouncedValue(typeQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const filterConfig: DataTableFilterConfig<SiteEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "route": {
        setQuery: setRouteQuery,
        query: routeQuery,
        debouncedQuery: routeDebouncedQuery,
        filter: ({routes}, query) => {
          const display = routes.join(", ")
          return !display.toLowerCase().includes(query.toLowerCase())
        }
      },
      "operatingArea": {
        setQuery: setOperatingAreaQuery,
        query: operatingAreaQuery,
        debouncedQuery: operatingAreaDebouncedQuery,
        filter: ({operatingAreaName}, query) => !operatingAreaName || !operatingAreaName.toLowerCase().includes(query.toLowerCase())
      },
      "type": {
        setQuery: setTypeQuery,
        query: typeQuery,
        debouncedQuery: typeDebouncedQuery,
        filter: ({type}, query) => {
          if (type) {
            const display = getFormattedDatabaseCode(type, t)
            return !type.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    routeQuery,
    routeDebouncedQuery,
    operatingAreaQuery,
    operatingAreaDebouncedQuery,
    typeQuery,
    typeDebouncedQuery,
    companyQuery,
    companyDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
